import { useEffect, useMemo } from "react";
import { api } from "../../lib/api";
import { JOB_POSTING_FIELDS, JOB_POSTING_LIMITS, jobPostingFieldError } from "../../lib/jobPostingValidation";
import { usePostingDraft } from "../../lib/usePostingDraft";
import { LocationPicker } from "../../components/LocationPicker";
import { TagPicker } from "../../components/SmartInputs";
import { ProductInformationInput } from "../../components/ProductInformationInput";
import { removeEmptyInapplicableProductInformation } from "../../data/itemProductInformation";
import { moveListProductInformationToDescription } from "../../data/itemDescriptionInformation";
import { projectDocumentError } from "../../lib/projectDocuments";
import { desiredMilestonesError, desiredPriceError } from "../../lib/rfqDesiredTerms";
import { RENTAL_RATE_PERIODS, DEFAULT_ITEM_SALE_UNIT, DEFAULT_ITEM_SALE_QUANTITY, PICKUP_LOCATION_REQUIRED_MESSAGE, QUICK_SUGGESTION_LIMIT, LOCATION_HISTORY_GROUPS, lastPostingLocation, displayCityName, EMPTY_POST_FORM, PURCHASE_FACT_FORM, collectionHoursFromDraft } from "./model";
import type { PostFlowStateModel } from "./usePostFlowState";

export function usePostFlowDraft(model: PostFlowStateModel) {
  const {
    type, current, translate, map, user, startStep, step, setStep, form, setForm, locationEdited,
    tagDraft, setTagDraft, locationDraft, setLocationDraft, collectionHours, setCollectionHours,
    rentalRatePeriod, setRentalRatePeriod, itemRules, setItemRules, setItemCatalogLoading,
    setItemCatalogError, itemCatalogAttempt, examples, suggestedTags, itemHandoverOptions,
    setItemHandoverOptions, error, setError, projectRequirements, setProjectRequirements,
    projectLicenses, setProjectLicenses, selectedProjectLicenses, setSelectedProjectLicenses,
    projectDocuments, setProjectDocuments, setCityOptions, setCityOptionsError,
    setProjectDateInputValid, setDeadlineInputValid, targetCompletionDateDraft,
    setTargetCompletionDateDraft, deadlineDraft, setDeadlineDraft, images, setImages, prevImages,
    setPrevImages, extraTitles, setExtraTitles, rfqItems, setRfqItems, rfqDesiredMilestones,
    setRfqDesiredMilestones, isServicesRfq, setResumeAttachments, setSelectedResumeId,
    setInitialActiveResumeId, setNewResumeIds
  } = model;
  const set = (k: string, v: any) => {
    if (k === "location") locationEdited.current = true;
    if (type === "job") setError(null);
    setForm((f) => ({ ...f, [k]: v }));
  };
  const productInformationField = <ProductInformationInput id="posting-product-information" itemType={form.itemType} value={form.productInformation} onChange={(value) => set("productInformation", value)} />;
  const invalidJobField = type === "job" ? JOB_POSTING_FIELDS.find((field) => jobPostingFieldError(field, form[field])) : undefined;
  const jobErrorKey = invalidJobField ? jobPostingFieldError(invalidJobField, form[invalidJobField]) : null;
  const jobDetailsError = jobErrorKey && invalidJobField ? translate(jobErrorKey).replace("{limit}", JOB_POSTING_LIMITS[invalidJobField].toLocaleString()) : null;
  const postingLocationField = <LocationPicker searchable draft={locationDraft} onDraftChange={setLocationDraft} key={`${user?.id ?? "guest"}:${type}`} value={form.location} invalid={type === "job" && Boolean(jobPostingFieldError("location", form.location))} describedBy={type === "job" ? "posting-job-location-feedback" : undefined} onChange={(display) => {
    set("location", display);
    if (type === "item" && error === PICKUP_LOCATION_REQUIRED_MESSAGE) setError(null);
  }} />;
  const postingTagField = <div><label className="label" htmlFor="posting-tags">{type === "job" ? translate("posting.requiredSkills") : type === "resume" ? "Skills" : "Tags"}</label><TagPicker draftValue={tagDraft} onDraftChange={setTagDraft} id="posting-tags" value={form.tags} onChange={(v) => set("tags", v)} suggestions={suggestedTags} suggestionLimit={QUICK_SUGGESTION_LIMIT} suggestionLabels={{ heading: translate("posting.tapToAdd"), more: translate("posting.moreSuggestions"), less: translate("posting.fewerSuggestions"), add: translate("posting.addInput") }} placeholder={examples.tags || (type === "job" ? "e.g. Read wiring diagrams — press Enter" : type === "resume" ? "Add a skill and press Enter" : "e.g. Cabinets, installation — press Enter")} /></div>;
  const setItemPrice = (amount: string) => {
    set("amount", amount);
  };
  // Mode changes clear incompatible choices so hidden sale-only types cannot remain in a rental draft.
  const setItemMode = (mode: "sale" | "rent") => {
    setRentalRatePeriod("day");
    setItemHandoverOptions([]);
    setForm((f) => {
      const itemType = itemRules.find((rule) => rule.value === f.itemType);
      const physicalSale = mode === "sale" && itemType?.quantityApplicable;
      const nextForm = { ...f, mode, amount: "", unit: physicalSale ? DEFAULT_ITEM_SALE_UNIT : "", stock: physicalSale ? DEFAULT_ITEM_SALE_QUANTITY : "" };
      return itemType?.modes.includes(mode) ? nextForm : { ...nextForm, kind: "", itemType: "", condition: "", businessOperatingStatus: "" };
    });
  };
  const setSelectedItemType = (value: string) => {
    const itemType = itemRules.find((rule) => rule.value === value);
    setItemHandoverOptions((options) => options.filter((option) => itemType?.handoverOptions.includes(option)));
    setForm((f) => {
      const previousType = itemRules.find((rule) => rule.value === f.itemType);
      const previousPricing = previousType?.kind === "business" || previousType?.kind === "property" ? previousType.kind : "physical";
      const nextPricing = itemType?.kind === "business" || itemType?.kind === "property" ? itemType.kind : "physical";
      const pricing = previousPricing === nextPricing ? {} : { amount: "", unit: "", stock: "" };
      // [items.listing-validation](../../../../docs/product/items.md#items-listing-validation): initialize a new physical sale without overwriting another physical type's draft choices.
      const defaults = f.mode === "sale" && itemType?.quantityApplicable && !previousType?.quantityApplicable
        ? { unit: DEFAULT_ITEM_SALE_UNIT, stock: DEFAULT_ITEM_SALE_QUANTITY } : {};
      return { ...f, ...pricing, ...defaults, itemType: value, kind: itemType?.kind || "", condition: "", businessOperatingStatus: itemType?.conditionProfile === "business" ? f.businessOperatingStatus : "" };
    });
  };
  const setItem = (i: number, k: string, v: string) => setRfqItems((rows) => rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addItem = () => setRfqItems((rows) => [...rows, { name: "", kind: "quantity", qty: "1", unit: "", spec: "", desiredPrice: "" }]);
  const removeItem = (i: number) => setRfqItems((rows) => rows.filter((_, j) => j !== i));
  const cleanItems = () => rfqItems.filter((r) => r.name.trim()).map((r) => {
    const desiredPrice = r.desiredPrice.trim() ? Number(r.desiredPrice) : undefined;
    return r.kind === "lump"
      ? { name: r.name.trim(), kind: "lump", spec: r.spec.trim(), desiredPrice }
      : { name: r.name.trim(), kind: "quantity", qty: Number(r.qty), unit: r.unit.trim(), spec: r.spec.trim(), desiredPrice };
  });
  // [rfq.request-type](../../../../docs/product/rfqs.md#rfq-request-type): Next and Publish enforce the same type-specific required content.
  function rfqDetailsError(): string | null {
    if (!form.requestType) return translate("rfq.type.required");
    if (isServicesRfq) return form.description.trim() ? null : "Describe the service you need to continue.";
    if (!cleanItems().length) return "Add at least one line item to continue.";
    if (rfqItems.some(row => row.name.trim() && row.kind !== "lump" && (!Number.isSafeInteger(Number(row.qty)) || Number(row.qty) < 1))) return "Item quantities must be positive whole numbers.";
    return null;
  }
  // [rfq.requested-terms](../../../../docs/product/rfqs.md#rfq-requested-terms): required Services stages and optional Items prices share Next/Publish validation.
  function rfqDesiredTermsError(): string | null {
    if (isServicesRfq) return desiredMilestonesError(rfqDesiredMilestones);
    for (const row of rfqItems.filter(row => row.name.trim())) {
      const priceError = desiredPriceError(row.desiredPrice);
      if (priceError) return priceError;
    }
    return null;
  }
  const cleanList = (values: string[]) => Array.from(new Set(values.map((value) => value.trim()).filter(Boolean))).slice(0, 12);
  const projectFiles = projectDocuments;
  function addProjectDocuments(list: FileList | null) {
    const incoming = Array.from(list || []);
    const invalidDocument = incoming.map(projectDocumentError).find((message) => message !== null);
    if (invalidDocument) { setError(invalidDocument); return; }
    setError(null);
    setProjectDocuments((current) => {
      const byFile = new Map(current.map((file) => [file.name + file.size, file]));
      for (const file of incoming) byFile.set(file.name + file.size, file);
      return Array.from(byFile.values());
    });
  }
  const draftSnapshot = {
    form: { ...form, pickupHours: JSON.stringify(collectionHours) }, step: Math.min(step, 5), rentalRatePeriod, itemHandoverOptions,
    projectRequirements: projectRequirements.map(row => ({ value: row.value, type: row.type || "" })),
    projectLicenses: projectLicenses.map(row => ({ value: row.value, type: row.type || "" })),
    selectedProjectLicenses, extraTitles, rfqItems, rfqDesiredMilestones, locationDraft, tagDraft, projectDateDraft: targetCompletionDateDraft, deadlineDraft,
  };
  const draftTemplate = { ...draftSnapshot, form: EMPTY_POST_FORM };
  const draftFiles = useMemo(() => ({ images, previousImages: prevImages, documents: projectDocuments, logo: [] as File[] }), [images, prevImages, projectDocuments]);
  const draft = usePostingDraft({
    actor: String(user?.id ?? "guest"), context: user?.activeContext, type, template: draftTemplate, snapshot: draftSnapshot, files: draftFiles,
    upgrade: (value) => {
      // Retain earlier profile-owned drafts while adding empty factual fields.
      // Existing values still pass the strict template validation below.
      if (typeof value !== "object" || value === null || !("form" in value)) return value;
      const savedForm = value.form;
      if (typeof savedForm !== "object" || savedForm === null || Array.isArray(savedForm)) return value;
      return { ...value, form: { productInformation: "", ...PURCHASE_FACT_FORM, ...savedForm } };
    },
    valid: value => Number.isInteger(value.step) && value.step >= 1 && value.step <= 5 && RENTAL_RATE_PERIODS.some(period => period === value.rentalRatePeriod),
    restore: (value, files, missing) => {
      // Quick-post entry skips a recovered type chooser; missing-file recovery
      // still returns to Media, and later saved steps keep their progress.
      setForm(value.form); setStep(missing ? 3 : Math.max(startStep, value.step));
      setCollectionHours(collectionHoursFromDraft(value.form.pickupHours));
      setRentalRatePeriod(value.rentalRatePeriod); setItemHandoverOptions(value.itemHandoverOptions);
      setProjectRequirements(value.projectRequirements); setProjectLicenses(value.projectLicenses);
      setSelectedProjectLicenses(value.selectedProjectLicenses); setExtraTitles(value.extraTitles);
      setRfqItems(value.rfqItems); setRfqDesiredMilestones(value.rfqDesiredMilestones);
      setLocationDraft(value.locationDraft); setTagDraft(value.tagDraft); setTargetCompletionDateDraft(value.projectDateDraft); setDeadlineDraft(value.deadlineDraft); locationEdited.current = true;
      setProjectDateInputValid(!value.projectDateDraft.trim() || Boolean(value.form.targetCompletionDate));
      setDeadlineInputValid(!value.deadlineDraft.trim() || Boolean(value.form.deadline));
      setImages(files.images); setPrevImages(files.previousImages); setProjectDocuments(files.documents);
    },
  });
useEffect(() => {
    if (type !== "item" || !draft.ready) return;
    const moved = moveListProductInformationToDescription(form.itemType, form.productInformation, form.description);
    if (moved.description === form.description && removeEmptyInapplicableProductInformation(form.itemType, moved.productInformation) === form.productInformation) return;
    setForm((current) => {
      const moved = moveListProductInformationToDescription(current.itemType, current.productInformation, current.description);
      const productInformation = removeEmptyInapplicableProductInformation(current.itemType, moved.productInformation);
      return productInformation === current.productInformation && moved.description === current.description ? current : { ...current, productInformation, description: moved.description };
    });
  }, [type, draft.ready, form.itemType, form.productInformation, form.description]);
useEffect(() => {
    let active = true;
    locationEdited.current = false;
    setForm((current) => ({ ...current, location: "" }));
    const group = LOCATION_HISTORY_GROUPS[type];
    if (!user?.id || (type !== "item" && !group)) return;
    const history = group ? api.listMine().then((owned) => owned[group]) : api.myItems();
    history.then((posts: unknown) => {
      const location = lastPostingLocation(type, posts);
      // A slow owner-history response must not overwrite edits or another account/type.
      if (!active || locationEdited.current || !location) return;
      setForm((current) => ({ ...current, location }));
    }).catch(() => { /* Optional defaults can fail; normal location entry stays available. */ });
    return () => { active = false; };
  }, [type, user?.id]);
  // [items.catalog](../../../../docs/product/items.md#items-catalog): failed catalog loads block type selection; local labels do not supply fallback rules.
  useEffect(() => {
    if (type !== "item" || !draft.ready) return;
    let active = true;
    setItemCatalogLoading(true);
    setItemCatalogError(false);
    api.itemPostingCatalog().then((catalog) => {
      if (!active) return;
      setItemRules(catalog.items);
      // [items.handover-options](../../../../docs/product/items.md#items-handover-options): discard retired choices from unsaved posting drafts, without rewriting listings.
      setItemHandoverOptions((choices) => choices.filter((option) => option !== "meetup"));
      if (!catalog.items.some((rule) => rule.modes.includes("rent"))) {
        setForm((current) => current.mode === "sale" ? current : { ...current, mode: "sale", amount: "", unit: "", stock: "" });
      }
      setItemCatalogLoading(false);
    }).catch(() => {
      if (!active) return;
      setItemCatalogError(true);
      setItemCatalogLoading(false);
    });
    return () => { active = false; };
  }, [type, itemCatalogAttempt, draft.ready]);
useEffect(() => {
    if (type !== "project") return;
    let active = true;
    api.geoCityOptions().then(({ items }) => {
      if (!active) return;
      setCityOptions(Array.from(new Set(items.map((item) => displayCityName(item.name)))).sort((a, b) => a.localeCompare(b)));
      setCityOptionsError(false);
    }).catch(() => { if (active) setCityOptionsError(true); });
    return () => { active = false; };
  }, [type]);
useEffect(() => {
    if (type !== "resume" || !user) return;
    api.myResumes().then((r) => {
      const rows = r?.attachments || [];
      const activeId = rows.find((a: any) => a.active)?.id || null;
      setResumeAttachments(rows);
      setInitialActiveResumeId(activeId);
      // Existing profile attachments are available choices, not an implicit
      // selection for this new résumé post.
      setSelectedResumeId(null);
      setNewResumeIds([]);
    }).catch(() => setResumeAttachments([]));
  }, [type, user?.id]);
  return {
    ...model, set, productInformationField, invalidJobField, jobErrorKey, jobDetailsError,
    postingLocationField, postingTagField, setItemPrice, setItemMode, setSelectedItemType,
    setItem, addItem, removeItem, cleanItems, rfqDetailsError, rfqDesiredTermsError, cleanList,
    projectFiles, addProjectDocuments, draftSnapshot, draftTemplate, draftFiles, draft
  };
}
export type PostFlowDraftModel = ReturnType<typeof usePostFlowDraft>;
